import type { PublicShipment } from '@/server/api';

export const PUBLIC_SHIPMENT_AVAILABILITY = {
  AVAILABLE: 'AVAILABLE',
  BOOK_PENDING: 'BOOK_PENDING',
  ON_HOLD: 'ON_HOLD',
  BOOKED: 'BOOKED',
} as const;

export type PublicShipmentAvailability =
  (typeof PUBLIC_SHIPMENT_AVAILABILITY)[keyof typeof PUBLIC_SHIPMENT_AVAILABILITY];

function normalizedAvailability(value: unknown) {
  if (typeof value !== 'string') return null;

  const normalized = value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

  switch (normalized) {
    case 'AVAILABLE':
      return PUBLIC_SHIPMENT_AVAILABILITY.AVAILABLE;
    case 'BOOK_PENDING':
    case 'PENDING_APPROVAL':
      return PUBLIC_SHIPMENT_AVAILABILITY.BOOK_PENDING;
    case 'ON_HOLD':
    case 'HOLD':
      return PUBLIC_SHIPMENT_AVAILABILITY.ON_HOLD;
    case 'BOOKED':
      return PUBLIC_SHIPMENT_AVAILABILITY.BOOKED;
    default:
      return null;
  }
}

/**
 * The public list did not originally expose booking state. Until the API adds
 * availability_status, keep existing loads actionable rather than treating an
 * unknown legacy shipment status as unavailable.
 */
export function getPublicShipmentAvailability(
  shipment: Pick<PublicShipment, 'availability_status' | 'booking_status'>
): PublicShipmentAvailability {
  return (
    normalizedAvailability(shipment.availability_status) ??
    normalizedAvailability(shipment.booking_status) ??
    PUBLIC_SHIPMENT_AVAILABILITY.AVAILABLE
  );
}

export function isPublicShipmentHeld(availability: PublicShipmentAvailability) {
  return (
    availability === PUBLIC_SHIPMENT_AVAILABILITY.BOOK_PENDING ||
    availability === PUBLIC_SHIPMENT_AVAILABILITY.ON_HOLD
  );
}

export function isPublicShipmentBooked(
  availability: PublicShipmentAvailability
) {
  return availability === PUBLIC_SHIPMENT_AVAILABILITY.BOOKED;
}

export function canPublicShipmentBid(shipment: PublicShipment) {
  const availability = getPublicShipmentAvailability(shipment);

  return (
    !isPublicShipmentHeld(availability) &&
    !isPublicShipmentBooked(availability) &&
    shipment.can_bid !== false
  );
}

export function canPublicShipmentBookNow(shipment: PublicShipment) {
  const availability = getPublicShipmentAvailability(shipment);

  return (
    typeof shipment.price === 'number' &&
    Number.isFinite(shipment.price) &&
    shipment.price > 0 &&
    !isPublicShipmentHeld(availability) &&
    !isPublicShipmentBooked(availability) &&
    shipment.can_book_now !== false
  );
}

export function bookingAvailabilityFromResponse(
  payload: unknown
): PublicShipmentAvailability {
  if (!payload || typeof payload !== 'object') {
    return PUBLIC_SHIPMENT_AVAILABILITY.BOOK_PENDING;
  }

  const response = payload as {
    availability_status?: unknown;
    data?: {
      availability_status?: unknown;
      shipment?: { availability_status?: unknown };
    };
  };

  return (
    normalizedAvailability(response.data?.shipment?.availability_status) ??
    normalizedAvailability(response.data?.availability_status) ??
    normalizedAvailability(response.availability_status) ??
    PUBLIC_SHIPMENT_AVAILABILITY.BOOK_PENDING
  );
}
